import { ICurrency } from '@/entities/currency';

import {
  VabsAchAccountType,
  VabsAchPaymentType,
  VabsBsbPaymentType,
  VabsExchangePaymentType,
  VabsIfscPaymentType,
  VabsInnerPaymentType,
  VabsParticipantType,
  VabsPaymentClientTypeEnum,
  VabsPaymentDirection,
  VabsPaymentInfoRequest,
  VabsPaymentSourceOfFunds,
  VabsPaymentSystem,
  VabsSwiftPaymentType,
  VabsWireAccountType,
  VabsWirePaymentType,
} from '@/shared/api';
import { IAddress, IBankInfo } from '@/shared/lib/types/currency-account';

export interface IPaymentTemplate {
  id: string;
  accType?: VabsAchAccountType | VabsWireAccountType;
  isFavorite?: boolean;
  direction: VabsPaymentDirection;
  account: string;
  incAmount: string;
  outAmount: string;
  currencyId: string;
  targetCurrencyId?: string;
  currency?: ICurrency;
  targetCurrency?: ICurrency;
  fee: string;
  type: VabsParticipantType;
  paymentSystem: VabsPaymentSystem;
  accountNumber: string;
  bankCode: string;
  firstName?: string;
  lastName?: string;
  companyName?: string;
  clientId?: string;
  country?: string;
  phoneNumber?: string;
  outTotalAmount?: string;
  incTotalAmount?: string;
  email?: string;
  intermediaryBank?:
    | {
        accountNumber?: string;
        swift?: string;
        bankInfo?: IBankInfo;
      }
    | undefined;
  recipientAddress: IAddress;
  address: IAddress;
  name: string; //bank name
  sourceOfFunds: VabsPaymentSourceOfFunds;
  purposeOfPayment: string;
  innerAccountId?: string;
  infoRequests: VabsPaymentInfoRequest[];
  note?: string;
}

export const serializePayment = (
  item:
    | VabsSwiftPaymentType
    | VabsAchPaymentType
    | VabsWirePaymentType
    | VabsIfscPaymentType
  | VabsBsbPaymentType
  | VabsInnerPaymentType
  | VabsExchangePaymentType,
  currencies: Map<string, ICurrency>,
): IPaymentTemplate => {
  const value = currencies.get(item.amount?.source?.currency?.id);
  const targetValue = currencies.get(item.amount?.target?.currency?.id);

  if (item.__typename === 'VabsInnerPaymentType') {
    //TODO: serialize payment and payment to form data for inners
    return {
      id: item.id,
      isFavorite: item.isFavorite,
      paymentSystem: VabsPaymentSystem.Inner,
      direction: item.direction,
      bankCode: '',
      firstName: '',
      lastName: '',
      country: '',
      outAmount: item.amount?.source?.amount,
      incAmount: item.amount?.target?.amount,
      currencyId: item.amount?.source?.currency?.id,
      targetCurrencyId: item.amount?.target?.currency?.id,
      currency: value,
      targetCurrency: targetValue,
      fee: item.fee?.source?.total,
      outTotalAmount: item.amount?.source?.amount,
      incTotalAmount: item.amount?.target?.amount,
      name: item.recipientClient.accountHolderName || '',
      purposeOfPayment: item.note || '',
      clientId: item.recipientClient?.clientId,
      phoneNumber: item.recipientClient.phoneNumber || '',
      email: item.recipientClient.email || '',
      innerAccountId: item.recipientClient.accountId,
      infoRequests: item.infoRequests?.nodes ?? [],
    };
  }

  if (item.__typename === 'VabsExchangePaymentType') {
    return {
      id: item.id,
      isFavorite: item.isFavorite,
      paymentSystem: VabsPaymentSystem.Exchange,
      direction: item.direction,
      bankCode: '',
      firstName: '',
      lastName: '',
      country: '',
      outAmount: item.amount?.source?.amount,
      incAmount: item.amount?.target?.amount,
      currencyId: item.amount?.source?.currency?.id,
      targetCurrencyId: item.amount?.target?.currency?.id,
      currency: value,
      targetCurrency: targetValue,
      fee: item.fee?.source?.total,
      outTotalAmount: item.amount?.source?.amount,
      incTotalAmount: item.amount?.target?.amount,
      name: item.recipientClient?.accountHolderName || '',
      purposeOfPayment: item.note || '',
      clientId: item.recipientClient?.clientId,
      phoneNumber: item.recipientClient?.phoneNumber || '',
      email: item.recipientClient?.email || '',
      innerAccountId: item.recipientClient?.accountId,
      infoRequests: item.infoRequests?.nodes ?? [],
      note: item.note || '',
    };
  } else {
    try {
      return {
        id: item.id,
        /** @deprecated */
        account: item.internal?.accountNumber || '', //do not use
        type:
          item?.external?.clientType === VabsPaymentClientTypeEnum.Corporate
            ? VabsParticipantType.B2B
            : VabsParticipantType.B2C,
        paymentSystem: item.paymentSystem,
         isFavorite: item.isFavorite,
        direction: item.direction,
        accountNumber: item.external?.accountNumber,
        bankCode: item.external?.bankCode,
        firstName: item.external?.firstName || '',
        lastName: item.external?.lastName || '',
        companyName: item.external?.accountHolderName || '',
        country: item.external?.bank?.country?.id,
        outAmount: item.amount?.source?.amount,
        incAmount: item.amount?.target?.amount,
        currencyId: item.amount?.source?.currency?.id,
        accType: item.external?.accountType || item.external?.accType, //graphql conflict if field is same name
        currency: value,
        fee: item.fee?.target?.total,
        intermediaryBank: item.external?.intermediaryBank?.bankCode
          ? {
              accountNumber:
                item.external?.intermediaryBank?.accountNumber || '',
              swift: item.external?.intermediaryBank?.bankCode || '',
              bankInfo: item.external?.intermediaryBank
                ? {
                    name: item.external?.intermediaryBank.name,
                    country: item.external?.intermediaryBank.country?.id,
                    street: item.external?.intermediaryBank.addressLine1 || '',
                    postcode: item.external?.intermediaryBank.addressLine2 || '',
                    city: item.external?.intermediaryBank.cityName || '',
                  }
                : undefined,
            }
          : undefined,
        recipientAddress: {
          country: item.external?.address?.country?.id || '',
          street: item.external?.address?.line2,
          postcode: item.external?.address.postalCode || '',
          city: item.external?.address.line1 || '',
        },
        email: '', //TODO no email in backend payment
        address: {
          //bank addr
          country: item.external?.bank.country?.id,
          street: item.external?.bank.addressLine1 || '',
          postcode: item.external?.bank.postalCode || '',
          city: item.external?.bank.cityName || '',
        },
        name: item.external?.bank.name || '', //bank name
        sourceOfFunds: item.sourceOfFunds,
        purposeOfPayment: item.purposeOfPayment,
        infoRequests: item.infoRequests?.nodes ?? [],
        note: item.note || "",
      };
    } catch (e) {
      throw new Error('err');
    }
  }
};
